import React, { type FC } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { buildMyTabbioRoute, buildTailoredCvRoute } from '../../../lib/cv-target-route';
import { CardShell } from './card-shell';
import {
  CardBadge,
  CardChevron,
  CardEmpty,
  CardFooterAction,
  CardHeader,
  CardRow,
} from './card-ui';
import { safeArray, safeGet } from './types';
import type { ToolCardProps } from './types';

type CvCardItem = {
  id: string;
  title?: string | null;
  summary?: string | null;
  isPublic?: boolean;
};

const MAX_DISPLAY = 4;

export const CVListCard: FC<ToolCardProps> = ({ result }) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const cvs = safeArray<CvCardItem>(result, 'cvs');
  const mainCv = safeGet<CvCardItem>(result, 'mainCv');
  const defaultCvId = safeGet<string>(result, 'defaultCvId') ?? null;
  const configuredMain = defaultCvId
    ? cvs.find((cv) => cv.id === defaultCvId)
    : mainCv;
  const ordered = [
    ...(configuredMain ? [configuredMain] : []),
    ...(mainCv && mainCv.id !== configuredMain?.id ? [mainCv] : []),
    ...cvs.filter((cv) => cv.id !== configuredMain?.id),
  ];
  const visible = ordered.slice(0, MAX_DISPLAY);

  if (!visible.length) {
    return (
      <CardShell>
        <CardEmpty>{t('chat.cvCard.empty')}</CardEmpty>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <CardHeader
        title={t('chat.cvCard.title')}
        meta={t('chat.cvCard.count', { count: ordered.length })}
        leading={
          <MaterialSymbol
            name="description"
            size={16}
            style={{ color: 'var(--text-tertiary)' }}
          />
        }
      />
      {visible.map((cv) => {
        const isMain = cv.id === configuredMain?.id;
        const isProfile = cv.id === 'master';
        const meta = isProfile
          ? t('chat.cvCard.profileBacked')
          : cv.isPublic
            ? t('chat.cvCard.public')
            : t('chat.cvCard.private');
        return (
          <CardRow
            key={cv.id}
            divider
            title={cv.title?.trim() || t('chat.cvCard.untitled')}
            subtitle={cv.summary?.trim() || undefined}
            meta={meta}
            onPress={() =>
              router.push(
                isProfile ? ('/cv?cvTarget=master' as never) : buildTailoredCvRoute(cv.id),
              )
            }
            accessibilityHint={t('chat.cvCard.openHint')}
            trailing={
              <>
                {isMain ? <CardBadge>{t('chat.cvCard.main')}</CardBadge> : null}
                <CardChevron />
              </>
            }
          />
        );
      })}
      {ordered.length > visible.length ? (
        <CardFooterAction
          label={t('chat.cvCard.showAll')}
          onPress={() => router.navigate(buildMyTabbioRoute())}
        />
      ) : null}
    </CardShell>
  );
};
